import LiveChatCRUDActions from '@mm/state/actions/LiveChatCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor } from '@xstate/react';
import classnames from 'classnames';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useContextSelector } from 'use-context-selector';
import { useCountdownTimer } from '../../../../../common/hooks/use-countdown-timer';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { LiveChatContext } from '../../LiveChatContext';
import { mutedData } from '../../shared';
import './BotPausing.pcss';
import { MuteBotSelector } from './MuteBotSelector';

export const INDEFINITELY = 99999999;

export const BotPausing = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const { remoteUserId, remoteFbPageId } = lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  const pauseBot = async ({
    mute,
    muteFor,
  }: {
    mute?: boolean;
    muteFor?: number;
  }) => {
    if (remoteFbPageId && remoteUserId) {
      await dispatch(
        LiveChatCRUDActions.updateSingleConversationStatus({
          remotePageId: remoteFbPageId,
          remoteUserIds: [remoteUserId],
          mute,
          muteFor,
        }),
      );

      lcMachineSend({
        type: 'LC_CONVERSATIONS_REFRESH',
        payload: [remoteUserId],
      });

      lcMachineSend({
        type: 'LC_CONVERSATIONS_REFILTER',
        payload: [remoteUserId],
      });
      refreshSelectedContact();
    }
  };

  const { mutedSecondsRemaining, mutedTillNumber } = mutedData(
    selectedContact?.conversation?.mutedTill,
  );

  const selectedConversationMuteCountDown = useCountdownTimer(mutedTillNumber);

  const isIndefinitely = !!(
    mutedSecondsRemaining && mutedSecondsRemaining > INDEFINITELY
  );
  const loading = !selectedContact;

  return remoteUserId ? (
    <div className="live-chat--messages__bot-pausing--container">
      <div
        className={classnames('live-chat--messages__bot-pausing', {
          'indefinitely-border': isIndefinitely,
        })}
        onClick={() => pauseBot({ mute: !selectedConversationMuteCountDown })}
      >
        {selectedConversationMuteCountDown ? (
          <>
            <i className="far fa-pause-circle live-chat--messages__bot-pausing_icon-circle" />
            Chatbot paused
            {isIndefinitely ? (
              <div className="live-chat--messages__bot-pausing_space-indefinitely">
                indefinitely
              </div>
            ) : (
              <>
                <div className="live-chat--messages__bot-pausing_space">
                  for
                </div>
                <div className="live-chat--messages__bot-pausing_time">
                  {selectedConversationMuteCountDown}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <i className="fas fa-play-circle live-chat--messages__bot-pausing_icon-circle" />
            {loading ? 'Loading...' : 'Chatbot is active'}
          </>
        )}
      </div>
      {selectedConversationMuteCountDown && (
        <MuteBotSelector
          isIndefinitely={isIndefinitely}
          mutedSecondsRemaining={mutedSecondsRemaining}
          onSelectTime={(value: number) =>
            pauseBot({
              muteFor: value,
            })
          }
        />
      )}
    </div>
  ) : null;
};
